import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { AuthLayout } from '../../components/layouts/AuthLayout';
import AuthFloatingInput from '../../components/auth/AuthFloatingInput';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import { slideUp } from '@/lib/motion';

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginError {
  email?: string;
  password?: string;
  general?: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState<LoginError>({});
  const [loading, setLoading] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Enter always submits, wherever it lands. Two failure modes are covered:
  // focus drifting off the form (body focus → no implicit submission), and
  // password-manager extensions cancelling the browser's implicit submission
  // outright (observed in the field: keydown reaches the password input but
  // the form never submits). So inside our inputs we submit explicitly and
  // preventDefault to avoid double-firing when implicit submission does work.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const target = e.target as HTMLElement | null;
      if (target && formRef.current?.contains(target)) {
        if (target.tagName === 'INPUT') {
          e.preventDefault();
          formRef.current.requestSubmit();
        }
        return; // buttons/links inside the form keep their native Enter
      }
      // Leave Enter alone wherever it already means something.
      if (target?.closest('form, a, button, [role="dialog"]')) return;
      formRef.current?.requestSubmit();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: LoginError = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name as keyof LoginError]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rateLimitCountdown !== null) {
      return; // Still rate limited
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const { requiresSelection } = await login({
        email: formData.email,
        password: formData.password,
      });
      // Members of multiple workspaces pick one first; everyone else goes to the
      // smart home, which lands them in their resolved view mode (Manage for
      // staff, Learning otherwise).
      navigate(requiresSelection ? '/auth/select-workspace' : '/');
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data;
      const apiMessage = data?.message || data?.error;
      const fieldErrors = data?.errors as Record<string, string[]> | undefined;

      if (status === 401) {
        setErrors({
          general: apiMessage || 'Invalid email or password. Please try again.',
        });
      } else if (status === 429) {
        const retryAfter = err.response?.headers['retry-after'] || 60;
        setRateLimitCountdown(parseInt(retryAfter));
        setErrors({
          general: apiMessage || `Too many login attempts. Please try again in ${retryAfter} seconds.`,
        });

        const interval = setInterval(() => {
          setRateLimitCountdown(prev => {
            if (prev === null || prev <= 1) {
              clearInterval(interval);
              return null;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (status === 400) {
        const mapped: LoginError = { general: apiMessage || 'Invalid request. Please check your input.' };
        if (fieldErrors?.email) mapped.email = fieldErrors.email[0];
        if (fieldErrors?.password) mapped.password = fieldErrors.password[0];
        setErrors(mapped);
      } else {
        setErrors({
          general: apiMessage || 'An error occurred. Please try again.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <motion.div {...slideUp}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-[26px] font-bold text-text-primary mb-2">
          Log in to your account
        </h1>
        <p className="text-sm text-text-secondary">
          Don&apos;t have an account?{' '}
          <Link
            to="/auth/signup"
            className="text-primary-500 font-medium hover:underline"
          >
            Get started
          </Link>
        </p>
      </div>

      {/* Login Form */}
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
        {/* Email Field */}
        <AuthFloatingInput
          label="Email address"
          id="email"
          name="email"
          type="email"
          autoFocus
          autoComplete="email"
          value={formData.email}
          onChange={handleChange}
          disabled={loading || rateLimitCountdown !== null}
          error={errors.email}
        />

        {/* Password Field */}
        <AuthFloatingInput
          label="Password"
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={formData.password}
          onChange={handleChange}
          disabled={loading || rateLimitCountdown !== null}
          error={errors.password}
        />

        {/* Forgot password link — after the password in DOM so Tab flows
            email → password → link, and Enter mid-form always submits. */}
        <div className="flex justify-end -mt-2">
          <Link
            to="/auth/forgot-password"
            className="text-sm text-text-primary hover:text-primary-500 transition-colors"
          >
            Forgot password?
          </Link>
        </div>

        {/* General Error — above submit button */}
        {errors.general && (
          <div className="p-3.5 bg-error-50 dark:bg-error-900/10 text-error-600 dark:text-error-400 rounded-lg text-sm">
            <p>{errors.general}</p>
            {rateLimitCountdown !== null && (
              <p className="font-medium mt-1.5">Retry in {rateLimitCountdown} seconds</p>
            )}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || rateLimitCountdown !== null}
          className="w-full h-12 rounded-full bg-primary-500 font-bold text-[15px] hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading
            ? 'Logging in...'
            : rateLimitCountdown !== null
              ? `Wait ${rateLimitCountdown}s`
              : 'Log in'}
        </button>
      </form>

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border-primary/20" />
        </div>
        <div className="relative flex justify-center">
          <span className="px-3 bg-white dark:bg-grey-950 text-xs text-text-secondary uppercase tracking-wider">
            or
          </span>
        </div>
      </div>

      {/* Social Login */}
      <div className="flex justify-center">
        <GoogleSignInButton
          mode="login"
          disabled={loading || rateLimitCountdown !== null}
        />
      </div>
      </motion.div>
    </AuthLayout>
  );
};

export default LoginPage;
